/**
 * useGraphStore — Zustand store for Cytoscape graph elements.
 *
 * Architecture (Phase C — Data/Display Separation):
 *
 *   Data layer:  each node's `expanded_transactions[]` holds ALL known transactions.
 *   Display layer:  `canvas_elements[]` (aliased as `elements[]`) holds what Cytoscape renders.
 *   Visibility:  `_visibility` data property on each element (0.0–1.0) controls opacity.
 *
 * Algorithms write to `expanded_transactions` first, then promote to canvas.
 * Eye toggle and graph filters set `_visibility`, never remove from canvas.
 * "Recompute Canvas" removes `_visibility === 0` elements (actual removal → layout reflow).
 *
 * See: docs/CANVAS_DATA_DISPLAY_SEPARATION.md
 */

import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import type { CytoscapeElement } from '../types/graph';
import { isControlledWrite } from '../services/canvasControlledWrite';
import { stripDataLayer } from '../utils/stripDataLayer';
import { createQuotaSafeSessionStorage } from '../utils/quotaSafeStorage';
import { useStashStore, type StashedElement } from './useStashStore';

/**
 * Phase 0 deprecation: warn when raw writers are called outside the
 * canvasController. Limited to 3 warnings per method per session to
 * avoid log spam.
 */
const _deprecationCounts = new Map<string, number>();
function warnIfUncontrolled(method: string): void {
    if (isControlledWrite()) return;
    // Vite's import.meta.env.PROD — avoids a bare `process` reference, which only
    // type-checks when @types/node leaks into the program via test files.
    if (import.meta.env.PROD) return;
    const seen = _deprecationCounts.get(method) ?? 0;
    if (seen >= 3) return;
    _deprecationCounts.set(method, seen + 1);
    // eslint-disable-next-line no-console
    console.warn(
        `[useGraphStore] ${method}() called directly. ` +
        `Route through canvasController (services/canvasController.ts) instead. ` +
        `This path will become an error in a future release.`,
    );
}

// ── Undo/Redo History ────────────────────────────────────────────────
// Snapshot-based: captures full elements[] before each user-initiated mutation.
// Max 20 snapshots. NOT persisted (sessionStorage only stores current elements).

const MAX_HISTORY = 20;

/**
 * One snapshot captures BOTH the canvas elements AND the stash so undo/redo
 * is coherent across presence changes (stash/restore). If stash isn't
 * captured, undoing a stash restores the elements but leaves zombie entries.
 */
export interface GraphSnapshot {
    elements: CytoscapeElement[];
    stashEntries: Record<string, StashedElement>;
}

export interface GraphState {
    /**
     * Canvas elements — what Cytoscape renders (nodes + edges).
     * This IS the display layer. Data layer lives in each node's `expanded_transactions`.
     *
     * Aliased as `elements` for backward compatibility.
     */
    elements: CytoscapeElement[];

    // ── Undo/Redo stacks (not persisted) ──
    /** Past snapshots — most recent last. Ctrl+Z pops from here. */
    _past: GraphSnapshot[];
    /** Future snapshots — for redo after undo. Cleared on new mutation. */
    _future: GraphSnapshot[];

    /**
     * Capture current elements as an undo snapshot BEFORE a mutation.
     * Call this before any user-initiated canvas change (add node, expand,
     * trace, FTM, filter, eye toggle, remove, clear).
     *
     * Do NOT call for: enrichment (entity label updates), SSE mid-stream
     * events, hover/selection changes, or programmatic batch updates that
     * aren't user-triggered.
     */
    pushSnapshot: () => void;

    /** Undo: restore the most recent snapshot. Returns true if undo was applied. */
    undo: () => boolean;

    /** Redo: re-apply a previously undone state. Returns true if redo was applied. */
    redo: () => boolean;

    /** How many undo steps are available. */
    undoDepth: () => number;

    /** How many redo steps are available. */
    redoDepth: () => number;

    /** Clear all history (call on New Investigation / session reset). */
    clearHistory: () => void;

    /** Replace the entire canvas elements array. */
    setElements: (els: CytoscapeElement[]) => void;

    /** Add elements to canvas. Deduplicates by data.id. */
    addElements: (els: CytoscapeElement[]) => void;

    /** Remove elements by their data.id (+ connected edges). */
    removeElements: (ids: string[]) => void;

    /** Update data properties on existing elements without replacing them. */
    enrichElements: (updates: Array<{ id: string; data: Record<string, unknown> }>) => void;

    /**
     * Mark a node as having had a report generated for it (C5/F4 provenance).
     * Appends `reportType` to `data.report_types` (deduped) and sets
     * `data.has_report`. Idempotent, keyed — one write site so a later
     * useGraphModel migration retargets a single function. Read by the canvas
     * report badge and the Stash Tray "Reported" group (one flag, two
     * projections — the R-SSOT-1 division-of-labor example).
     */
    markNodeReported: (nodeId: string, reportType: string) => void;

    /**
     * Set visibility on a single element (Phase A).
     * Updates `_visibility` data property. Does NOT remove from array.
     */
    setVisibility: (id: string, level: number) => void;

    /**
     * Set visibility on multiple elements at once (Phase A).
     * Used by graph filters and Follow Money mode.
     */
    setVisibilityBulk: (updates: Map<string, number>) => void;

}

export const useGraphStore = create<GraphState>()(
    persist(
    (set, get) => ({
    elements: [],
    _past: [],
    _future: [],

    pushSnapshot: () => {
        const current = get().elements;
        const stashEntries = useStashStore.getState().entries;
        // Don't push if both sides are empty and there's no history
        if (current.length === 0 && Object.keys(stashEntries).length === 0 && get()._past.length === 0) return;
        set(state => {
            const snapshot: GraphSnapshot = { elements: current, stashEntries: { ...stashEntries } };
            const past = [...state._past, snapshot];
            if (past.length > MAX_HISTORY) past.shift();
            return { _past: past, _future: [] };
        });
    },

    undo: () => {
        const { _past, elements } = get();
        if (_past.length === 0) return false;
        const previous = _past[_past.length - 1];
        const currentStash = useStashStore.getState().entries;
        set({
            elements: previous.elements,
            _past: _past.slice(0, -1),
            _future: [{ elements, stashEntries: currentStash }, ...get()._future],
        });
        useStashStore.getState().hydrate(previous.stashEntries);
        return true;
    },

    redo: () => {
        const { _future, elements } = get();
        if (_future.length === 0) return false;
        const next = _future[0];
        const currentStash = useStashStore.getState().entries;
        set({
            elements: next.elements,
            _past: [...get()._past, { elements, stashEntries: currentStash }],
            _future: _future.slice(1),
        });
        useStashStore.getState().hydrate(next.stashEntries);
        return true;
    },

    undoDepth: () => get()._past.length,
    redoDepth: () => get()._future.length,

    clearHistory: () => set({ _past: [], _future: [] }),

    setElements: (els) => { warnIfUncontrolled('setElements'); set({ elements: els }); },

    addElements: (els) => {
        warnIfUncontrolled('addElements');
        const current = get().elements;
        const existingIds = new Set(current.map(el => el.data.id));
        const newEls = els.filter(el => !existingIds.has(el.data.id));
        if (newEls.length === 0) return;
        set({ elements: [...current, ...newEls] });
    },

    removeElements: (ids) => {
        warnIfUncontrolled('removeElements');
        const idSet = new Set(ids);
        const current = get().elements;
        const filtered = current.filter(el => {
            if (idSet.has(el.data.id)) return false;
            if (el.data.source && (idSet.has(el.data.source) || (el.data.target && idSet.has(el.data.target)))) {
                return false;
            }
            return true;
        });
        set({ elements: filtered });
    },

    enrichElements: (updates) => {
        const current = get().elements;
        const updateMap = new Map(updates.map(u => [u.id, u.data]));
        let changed = false;
        const next = current.map(el => {
            const patch = updateMap.get(el.data.id);
            if (!patch) return el;
            changed = true;
            return {
                ...el,
                data: { ...el.data, ...patch },
            };
        });
        if (changed) {
            set({ elements: next });
        }
    },

    markNodeReported: (nodeId, reportType) => {
        const current = get().elements;
        let changed = false;
        const next = current.map(el => {
            if (el.data.id !== nodeId || el.data.source) return el;
            const existing: string[] = Array.isArray(el.data.report_types)
                ? (el.data.report_types as string[]) : [];
            if (existing.includes(reportType)) return el;
            changed = true;
            return { ...el, data: { ...el.data, report_types: [...existing, reportType], has_report: true } };
        });
        if (changed) set({ elements: next });
    },

    setVisibility: (id, level) => {
        warnIfUncontrolled('setVisibility');
        const current = get().elements;
        const next = current.map(el => {
            if (el.data.id !== id) return el;
            if (el.data._visibility === level) return el;
            return { ...el, data: { ...el.data, _visibility: level } };
        });
        set({ elements: next });
    },

    setVisibilityBulk: (updates) => {
        warnIfUncontrolled('setVisibilityBulk');
        const current = get().elements;
        let changed = false;
        const next = current.map(el => {
            const level = updates.get(el.data.id);
            if (level === undefined || el.data._visibility === level) return el;
            changed = true;
            return { ...el, data: { ...el.data, _visibility: level } };
        });
        if (changed) set({ elements: next });
    },

}),
    {
        name: 'workbench-graph',
        // Quota-safe persist — QuotaExceededError on setItem must not
        // bubble into React render. Shared helper also used by
        // useTransactionStore (`workbench-transactions` is the key that
        // historically crashed the Workbench after graph payloads were
        // stripped). Persistence is best-effort; Save Snapshot is durable.
        storage: createJSONStorage(() => createQuotaSafeSessionStorage({
            warnKey: '__workbench_graph_quota_warned__',
            warnMessage:
                '[useGraphStore] sessionStorage quota exceeded — graph will not persist across reloads. ' +
                'Use Save Snapshot to keep this investigation.',
        })),
        version: 1,
        // Persist the display layer only. Strip per-node transaction
        // payload (`expanded_transactions`, `token_transfers`,
        // `internal_txs`) before write — that's data-layer state owned by
        // `useTransactionStore` and refetched on next load. Without this
        // strip, a 2000-node investigation blows past sessionStorage's
        // ~5 MB cap and the persist write fails (see CLAUDE.md R12).
        // Shared with Workbench.executeSave via stripDataLayer — keeping both
        // persist paths on one implementation is what prevents the asymmetry
        // (sessionStorage stripped, server save didn't) that bloated snapshots.
        partialize: (state) => ({
            elements: stripDataLayer(state.elements),
        }) as any,
    }
));

// Expose store on window for E2E test introspection. Mirrors the
// unconditional `window.__cy = cy` exposure in GraphCanvas — read-only
// reference to canvas elements, no security risk.
if (typeof window !== 'undefined') {
    (window as any).__useGraphStore = useGraphStore;
}
